import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ScrollPage from "../../Components/scrollpageUp";
import "./style.css";
import homeVideo from "../../videos/home_video.mp4";
import cuisines from "../../images/Home/Cuisines.jpg";
import dressings from "../../images/Home/Dressings.jpg";
import amenagements from "../../images/Home/Aménagement intérieur.jpg";
import meubles from "../../images/Home/Meubles salles de bain et buanderies.jpg";
import accessoires from "../../images/Home/Accessoires & solutions de rangement.jpg";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

function Home() {
  const location = useLocation();
  const navigate = useNavigate();

  const containerRef = useRef(null);
  const activeCardRef = useRef(null);
  const spacesRef = useRef(null);
  const heroContentRef = useRef(null);
  const spacesHeadRef = useRef(null);
  const cardsRef = useRef([]);

  const [isDragging, setIsDragging] = useState(false);

  // ─── GSAP Animations ──────────────────────────────────────────
  useEffect(() => {
    const ctx = gsap.context(() => {

      // 1. Hero content — apparition au chargement
      gsap.fromTo(
        heroContentRef.current,
        { opacity: 0, y: 60 },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: "power3.out",
          delay: 0.3,
        }
      );

      // 2. Section title — fade in au scroll
      gsap.fromTo(
        spacesHeadRef.current,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: spacesHeadRef.current,
            start: "top 85%",
            once: true,
          },
        }
      );

      // 3. Cards — stagger au scroll
      gsap.fromTo(
        cardsRef.current,
        { opacity: 0, y: 80, scale: 0.95 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.7,
          ease: "power3.out",
          stagger: 0.15,
          scrollTrigger: {
            trigger: spacesRef.current,
            start: "top 75%",
            once: true,
          },
        }
      );

    });

    return () => ctx.revert();
  }, []);

  // ─── Scroll to active card ─────────────────────────────────────
  useEffect(() => {
    if (activeCardRef.current) {
      activeCardRef.current.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [location.pathname]);

  // ─── Drag & Wheel scroll ───────────────────────────────────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handleWheel = (e) => {
      if (Math.abs(e.deltaY) > 0) {
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      }
    };

    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;

    const handleMouseDown = (e) => {
      isDown = true;
      setIsDragging(true);
      startX = e.pageX - el.offsetLeft;
      scrollLeft = el.scrollLeft;
    };

    const handleMouseLeave = () => { isDown = false; setIsDragging(false); };
    const handleMouseUp = () => { isDown = false; setIsDragging(false); };

    const handleMouseMove = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - el.offsetLeft;
      el.scrollLeft = scrollLeft - (x - startX);
    };

    el.addEventListener("wheel", handleWheel, { passive: false });
    el.addEventListener("mousedown", handleMouseDown);
    el.addEventListener("mouseleave", handleMouseLeave);
    window.addEventListener("mouseup", handleMouseUp);
    el.addEventListener("mousemove", handleMouseMove);

    return () => {
      el.removeEventListener("wheel", handleWheel);
      el.removeEventListener("mousedown", handleMouseDown);
      el.removeEventListener("mouseleave", handleMouseLeave);
      window.removeEventListener("mouseup", handleMouseUp);
      el.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  // ─── Arrow scroll ──────────────────────────────────────────────
  const scrollNext = () => {
    const container = containerRef.current;
    if (!container) return;
    const item = container.querySelector(".spacesItem");
    if (!item) return;
    const gap = parseFloat(window.getComputedStyle(container).columnGap || 0);
    container.scrollBy({ left: item.offsetWidth + gap, behavior: "smooth" });
  };

  const scrollPrev = () => {
    const container = containerRef.current;
    if (!container) return;
    const item = container.querySelector(".spacesItem");
    if (!item) return;
    const gap = parseFloat(window.getComputedStyle(container).columnGap || 0);
    container.scrollBy({ left: -(item.offsetWidth + gap), behavior: "smooth" });
  };

  const handleNavigate = (path) => {
    if (!isDragging) navigate(path);
  };

  const spaces = [
    { path: "/collections/cuisines",  title: "Cuisines",                              img: cuisines     },
    { path: "/collections/dressings", title: "Dressings",                             img: dressings    },
    { path: "#",                       title: "Aménagement intérieur",                 img: amenagements },
    { path: "#",                       title: "Meubles salles de bain et buanderies",  img: meubles      },
    { path: "#",                       title: "Accessoires & solutions de rangement",  img: accessoires  },
  ];

  return (
    <div className="home">
      <ScrollPage />

      {/* ── Hero ── */}
      <section className="hero">
        <video className="heroVideo" autoPlay loop muted playsInline>
          <source src={homeVideo} type="video/mp4" />
        </video>
        <div className="heroOverlay"></div>

        {/* ref sur heroContent — plus besoin de la classe revealHero */}
        <div className="heroContent" ref={heroContentRef}>
          <span className="heroSubTitle">Raffinement • Intemporel • Premium</span>
          <h1>Vos rêves nous inspirent …</h1>
          <p>
            Cuisines, dressings, salles de bain et espaces de vie entièrement personnalisés.
          </p>
        </div>
      </section>

      {/* ── Spaces ── */}
      <section ref={spacesRef} className="homeSpaces">

        <div className="spacesHead" ref={spacesHeadRef}>
          <span className="spacesMiniTitle">Découvrez nos réalisations uniques.</span>
          <p lang="fr">
            Nous créons des intérieurs d'exception où luxe, personnalisation et savoir-faire
            se rencontrent. Des espaces élégants et fonctionnels, réalisés avec des matériaux
            premium et des finitions haut de gamme.
          </p>
        </div>

        <div className="spacesScrollWrap">
          <button
            className="arrow left"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={scrollPrev}
            aria-label="scroll left"
          >‹</button>

          <ul className="spacesScrollList" ref={containerRef}>
            {spaces.map((item, i) => {
              const active = location.pathname === item.path;
              return (
                <li
                  key={item.path}
                  className="spacesItem"
                  // ✅ collecte chaque card dans le tableau ref
                  ref={(el) => (cardsRef.current[i] = el)}
                >
                  {/* <a href> réel → lien crawlable par les moteurs de recherche */}
                  <a
                    href={item.path}
                    ref={active ? activeCardRef : null}
                    className={`spaceCard ${active ? "active" : ""}`}
                    draggable="false"
                    onClick={(e) => {
                      e.preventDefault();
                      if (item.path !== "#") handleNavigate(item.path);
                    }}
                  >
                    <div className="spaceImageWrap">
                      <img
                        src={item.img}
                        alt={item.title}
                        className="spaceImage"
                        draggable="false"
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="spaceOverlay">
                        <h3 className="spaceTitle">{item.title}</h3>
                      </div>
                    </div>
                  </a>
                </li>
              );
            })}
          </ul>

          <button
            className="arrow right"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={scrollNext}
            aria-label="scroll right"
          >›</button>
        </div>
      </section>
    </div>
  );
}

export default Home;